// Simple SQL validation utility
// This is a basic validator. For more advanced validation, consider integrating a SQL parser library.

// Forbidden SQL keywords (case-insensitive, word boundaries)
const forbidden =
  /\b(INSERT|UPDATE|DELETE|DROP|ALTER|TRUNCATE|CREATE|REPLACE|GRANT|REVOKE|MERGE|CALL|EXEC|EXECUTE|DESCRIBE|SHOW|USE|SET|PRAGMA|ATTACH|DETACH|BEGIN|COMMIT|ROLLBACK)\b/i;

/**
 * Validates a SQL query string to ensure it is safe and follows basic rules.
 * - Must not be empty
 * - Must not contain multiple statements (no semicolons except at the end)
 * - Must not contain comments
 * - Must not contain forbidden statements (e.g., DDL/DML)
 * - Must start with SELECT
 */
export function validateSQLQuery(query: string): string | null {
  if (!query || !query.trim()) {
    return 'Query cannot be empty.';
  }
  // Disallow multiple statements (no semicolons except at the end, and not required)
  const semicolonMatch = query.match(/;/g);
  if (semicolonMatch && semicolonMatch.length > 1) {
    return 'Only a single SQL statement is allowed.';
  }
  if (/;\s*[^\s]*\S/.test(query)) {
    return 'Only a single SQL statement is allowed.';
  }
  // Disallow comments (to prevent SQL injection via comments)
  if (/--|\/\*/.test(query)) {
    return 'SQL comments are not allowed.';
  }
  // Basic check for forbidden statements (e.g., DDL/DML)
  if (forbidden.test(query)) {
    return 'Only safe SELECT queries are allowed.';
  }
  // Must start with SELECT (optionally with whitespace)
  if (!/^\s*SELECT\b/i.test(query)) {
    return 'Query must start with SELECT.';
  }

  // Disallow reference to project_id in the query
  if (/\bproject_id\b/i.test(query)) {
    return 'Queries cannot reference project_id directly. Use the provided project_id constraint.';
  }

  return null; // No error
}

/**
 * Injects a project_id constraint into a SQL SELECT query to ensure users can only access their own project's data
 */
export function injectProjectIdConstraint(
  query: string,
  projectId: string,
): string {
  if (!query || !projectId) {
    return query;
  }

  // Remove trailing semicolons and whitespace for easier processing
  let cleanQuery = query.trim().replace(/;+\s*$/, '');

  // Check if the query already contains project_id in WHERE clause
  // This prevents users from trying to bypass security by specifying their own project_id conditions
  // We need to be more specific - only check WHERE clauses, not SELECT clauses
  const whereMatch = cleanQuery.match(/\bWHERE\b/i);
  if (whereMatch) {
    const whereIndex = whereMatch.index!;
    const whereClause = cleanQuery.substring(whereIndex);
    if (/\bproject_id\b/i.test(whereClause)) {
      throw new Error(
        'Queries cannot contain project_id conditions in WHERE clause. Project access is automatically enforced.',
      );
    }
  }

  // Escape single quotes in projectId to prevent SQL injection
  const escapedProjectId = projectId.replace(/'/g, "''");
  const projectIdConstraint = `project_id = '${escapedProjectId}'`;

  // Check if query already has a WHERE clause (case-insensitive)
  // We already found it above, so we can reuse the whereMatch
  if (whereMatch) {
    // Query already has a WHERE clause, so we need to add AND condition
    const whereIndex = whereMatch.index!;
    const beforeWhere = cleanQuery.substring(
      0,
      whereIndex + whereMatch[0].length,
    );
    const afterWhere = cleanQuery.substring(whereIndex + whereMatch[0].length);

    // Find the end of the WHERE clause (before GROUP BY, ORDER BY, LIMIT, HAVING, FORMAT)
    const clausePattern = /\b(GROUP\s+BY|ORDER\s+BY|LIMIT|HAVING|FORMAT)\b/i;
    const clauseMatch = afterWhere.match(clausePattern);

    if (clauseMatch) {
      // Split the WHERE conditions from the rest of the query
      const whereConditions = afterWhere
        .substring(0, clauseMatch.index!)
        .trim();
      const restOfQuery = afterWhere.substring(clauseMatch.index!);

      // Add the project_id constraint with AND, wrapping only the WHERE conditions
      cleanQuery = `${beforeWhere} ${projectIdConstraint} AND (${whereConditions}) ${restOfQuery}`;
    } else {
      // No clauses after WHERE, so the entire afterWhere is WHERE conditions
      cleanQuery = `${beforeWhere} ${projectIdConstraint} AND (${afterWhere.trim()})`;
    }
  } else {
    // No WHERE clause exists, so we need to add one
    // Find the position to insert WHERE clause (before GROUP BY, ORDER BY, LIMIT, HAVING, etc.)
    const clausePattern = /\b(GROUP\s+BY|ORDER\s+BY|LIMIT|HAVING|FORMAT)\b/i;
    const clauseMatch = cleanQuery.match(clausePattern);

    if (clauseMatch) {
      // Insert WHERE before the first clause found
      const clauseIndex = clauseMatch.index!;
      const beforeClause = cleanQuery.substring(0, clauseIndex).trim();
      const fromClause = cleanQuery.substring(clauseIndex);

      cleanQuery = `${beforeClause} WHERE ${projectIdConstraint} ${fromClause}`;
    } else {
      // No special clauses found, just append WHERE at the end
      cleanQuery = `${cleanQuery} WHERE ${projectIdConstraint}`;
    }
  }

  return cleanQuery;
}
